import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import {
    Zap,
    Layers,
    Smartphone,
    Monitor,
    RefreshCw,
    Archive,
    ChevronDown,
    ChevronRight,
    DollarSign,
    Hash,
    Info,
    Copy,
    Check,
    Search,
    Globe,
} from 'lucide-react';
import {
    getDomains,
    getCascadeBlocks,
    getCascadeDefaultSteps,
    getCascadeGranulations,
    generateCascadeBlocks,
    revampCascadeBlocks,
} from '@/services/api';
import type {
    GenerateCascadeBlocksRequest,
    RevampCascadeBlocksRequest,
    CascadeBlockGroup,
    CascadeBlock,
} from '@/types/cascade-blocks';
import type { CascadeGranulation } from '@/types/price-rules';

const CASCADE_BLOCK_TYPES = [
    { value: 'Topo', label: 'Topo' },
    { value: 'Rewarded', label: 'Rewarded' },
    { value: 'Interstitial', label: 'Interstitial' },
];

const FORMAT_TYPES = [
    { value: 'MOBILE', label: 'Mobile', icon: Smartphone },
    { value: 'WEB', label: 'Web', icon: Monitor },
];

export default function CascadeBlocks() {
    // State - dialogs
    const [isGenerateOpen, setIsGenerateOpen] = useState(false);
    const [isRevampOpen, setIsRevampOpen] = useState(false);

    // State - generate form
    const [genDomainId, setGenDomainId] = useState<number>(0);
    const [genBlockType, setGenBlockType] = useState<string>('Rewarded');
    const [genFormatType, setGenFormatType] = useState<string>('MOBILE');
    const [genMode, setGenMode] = useState<'default' | 'custom'>('default');
    const [genSelectedGranulation, setGenSelectedGranulation] = useState<CascadeGranulation | null>(null);

    // State - revamp form
    const [revDomainId, setRevDomainId] = useState<number>(0);
    const [revBlockType, setRevBlockType] = useState<string>('Rewarded');
    const [revFormatType, setRevFormatType] = useState<string>('MOBILE');
    const [revMode, setRevMode] = useState<'default' | 'custom'>('default');
    const [revSelectedGranulation, setRevSelectedGranulation] = useState<CascadeGranulation | null>(null);

    // State - listagem
    const [listDomainId, setListDomainId] = useState<number>(0);
    const [listBlockType, setListBlockType] = useState<string>('');
    const [listFormatType, setListFormatType] = useState<string>('');
    const [includeArchived, setIncludeArchived] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<string[]>([]);

    // State - searchable dropdowns
    const [listDomainOpen, setListDomainOpen] = useState(false);
    const [listDomainSearch, setListDomainSearch] = useState('');
    const [genDomainOpen, setGenDomainOpen] = useState(false);
    const [genDomainSearch, setGenDomainSearch] = useState('');
    const [revDomainOpen, setRevDomainOpen] = useState(false);
    const [revDomainSearch, setRevDomainSearch] = useState('');
    const [listBlockTypeOpen, setListBlockTypeOpen] = useState(false);
    const [listFormatTypeOpen, setListFormatTypeOpen] = useState(false);

    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Queries
    const { data: domains } = useQuery({
        queryKey: ['domains'],
        queryFn: getDomains,
    });

    const { data: defaultSteps } = useQuery({
        queryKey: ['cascadeDefaultSteps'],
        queryFn: getCascadeDefaultSteps,
    });

    const { data: granulations } = useQuery({
        queryKey: ['cascadeGranulations'],
        queryFn: getCascadeGranulations,
    });

    const {
        data: cascadeData,
        isLoading: isLoadingBlocks,
        refetch: refetchBlocks,
    } = useQuery({
        queryKey: ['cascadeBlocks', listDomainId, listBlockType, listFormatType, includeArchived],
        queryFn: () => getCascadeBlocks(listDomainId, listBlockType || undefined, listFormatType || undefined, includeArchived),
        enabled: listDomainId > 0,
    });

    // Mutations
    const generateMutation = useMutation({
        mutationFn: generateCascadeBlocks,
        onSuccess: (data) => {
            toast({
                title: 'Cascade blocks gerados!',
                description: `${data.stats.created} blocos criados, ${data.stats.existing} já existentes. Grupo: ${data.cascade_info.cascade_group}`,
            });
            queryClient.invalidateQueries({ queryKey: ['cascadeBlocks'] });
            setIsGenerateOpen(false);
            resetGenerateForm();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao gerar cascade blocks',
                description: error?.response?.data?.detail || error.message,
            });
        },
    });

    const revampMutation = useMutation({
        mutationFn: revampCascadeBlocks,
        onSuccess: (data) => {
            toast({
                title: 'Revamp concluído!',
                description: `${data.stats.archived} blocos arquivados, ${data.stats.created} novos criados. Novo grupo: ${data.stats.new_group}`,
            });
            queryClient.invalidateQueries({ queryKey: ['cascadeBlocks'] });
            setIsRevampOpen(false);
            resetRevampForm();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro no revamp',
                description: error?.response?.data?.detail || error.message,
            });
        },
    });

    // Helpers
    const getFaviconUrl = (domain: string) => `https://www.google.com/s2/favicons?domain=${domain}&sz=32`;

    const filteredListDomains = domains?.filter((d) =>
        (d.name || d.domain).toLowerCase().includes(listDomainSearch.toLowerCase())
    ) || [];

    const filteredGenDomains = domains?.filter((d) =>
        (d.name || d.domain).toLowerCase().includes(genDomainSearch.toLowerCase())
    ) || [];

    const filteredRevDomains = domains?.filter((d) =>
        (d.name || d.domain).toLowerCase().includes(revDomainSearch.toLowerCase())
    ) || [];

    const listSelectedDomain = domains?.find((d) => d.id === listDomainId);
    const genSelectedDomain = domains?.find((d) => d.id === genDomainId);
    const revSelectedDomain = domains?.find((d) => d.id === revDomainId);

    const resetGenerateForm = () => {
        setGenDomainId(0);
        setGenBlockType('Rewarded');
        setGenFormatType('MOBILE');
        setGenMode('default');
        setGenSelectedGranulation(null);
    };

    const resetRevampForm = () => {
        setRevDomainId(0);
        setRevBlockType('Rewarded');
        setRevFormatType('MOBILE');
        setRevMode('default');
        setRevSelectedGranulation(null);
    };

    const getStepsForPreview = (blockType: string, mode: 'default' | 'custom', granulation: CascadeGranulation | null) => {
        if (mode === 'custom' && granulation) {
            return granulation.steps;
        }
        return defaultSteps?.[blockType]?.steps || [];
    };

    const toggleGroup = (group: string) => {
        setExpandedGroups((prev) =>
            prev.includes(group) ? prev.filter((g) => g !== group) : [...prev, group]
        );
    };

    const copyPath = (path: string) => {
        navigator.clipboard.writeText(path);
        toast({ title: 'Path copiado!' });
    };

    // Handlers
    const handleGenerate = () => {
        if (!genDomainId) {
            toast({ variant: 'destructive', title: 'Selecione um domínio' });
            return;
        }

        const request: GenerateCascadeBlocksRequest = {
            domain_id: genDomainId,
            block_type: genBlockType,
            format_type: genFormatType,
        };

        if (genMode === 'custom' && genSelectedGranulation) {
            request.custom_steps = genSelectedGranulation.steps;
        }

        generateMutation.mutate(request);
    };

    const handleRevamp = () => {
        if (!revDomainId) {
            toast({ variant: 'destructive', title: 'Selecione um domínio' });
            return;
        }

        const request: RevampCascadeBlocksRequest = {
            domain_id: revDomainId,
            block_type: revBlockType,
            format_type: revFormatType,
        };

        if (revMode === 'custom' && revSelectedGranulation) {
            request.custom_steps = revSelectedGranulation.steps;
        }

        revampMutation.mutate(request);
    };

    const getStatusBadge = (status: string) => {
        if (status === 'ACTIVE') return <Badge variant="success">Ativo</Badge>;
        return <Badge variant="destructive">Arquivado</Badge>;
    };

    // Contadores
    const activeGroups = cascadeData?.groups?.filter((g) => g.status === 'ACTIVE') || [];
    const archivedGroups = cascadeData?.groups?.filter((g) => g.status === 'ARCHIVED') || [];

    // Componente reutilizável para seleção de granulação
    const GranulationSelector = ({
        selected,
        onSelect,
        disabled,
        accentColor = 'blue',
    }: {
        selected: CascadeGranulation | null;
        onSelect: (g: CascadeGranulation | null) => void;
        disabled: boolean;
        accentColor?: 'blue' | 'orange';
    }) => {
        const borderColor = accentColor === 'orange' ? 'border-orange-500' : 'border-blue-600';
        const bgColor = accentColor === 'orange' ? 'bg-orange-50' : 'bg-blue-50';

        if (!granulations || granulations.length === 0) {
            return (
                <div className="rounded-lg border border-dashed border-gray-300 p-4 text-center text-sm text-gray-500">
                    Nenhuma granulação cadastrada. Crie uma em Price Cascade &gt; Granulações.
                </div>
            );
        }

        return (
            <div className="space-y-2 max-h-52 overflow-y-auto">
                {granulations.map((g) => {
                    const isSelected = selected?.id === g.id;
                    const stepsArr = g.steps || [];
                    const maxPrice = stepsArr.length > 0 ? Math.max(...stepsArr) : 0;
                    const minPrice = stepsArr.length > 0 ? Math.min(...stepsArr) : 0;

                    return (
                        <button
                            key={g.id}
                            type="button"
                            onClick={() => onSelect(isSelected ? null : g)}
                            disabled={disabled}
                            className={`w-full p-3 rounded-lg border-2 transition-all text-left flex items-center justify-between
                                ${isSelected ? `${borderColor} ${bgColor}` : 'border-gray-200 hover:border-gray-300'}
                                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                            `}
                        >
                            <div className="flex items-center gap-3 min-w-0">
                                {isSelected && (
                                    <Check className={`h-4 w-4 flex-shrink-0 ${accentColor === 'orange' ? 'text-orange-600' : 'text-blue-600'}`} />
                                )}
                                <div className="min-w-0">
                                    <p className="font-medium text-sm text-gray-900 truncate">{g.name}</p>
                                    <p className="text-xs text-gray-500">
                                        {stepsArr.length} steps &middot; ${maxPrice} &rarr; ${minPrice}
                                    </p>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>
        );
    };

    return (
        <div className="space-y-4 md:space-y-6 p-4 md:p-0">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Cascade Blocks</h1>
                    <p className="mt-1 md:mt-2 text-sm md:text-base text-gray-600">
                        Blocos individuais por price step para Topo, Rewarded e Interstitial
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={() => setIsRevampOpen(true)}
                        variant="outline"
                        className="border-orange-300 text-orange-700 hover:bg-orange-50"
                    >
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Revamp
                    </Button>
                    <Button
                        onClick={() => setIsGenerateOpen(true)}
                        className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                    >
                        <Zap className="mr-2 h-4 w-4" />
                        Gerar Blocos
                    </Button>
                </div>
            </div>

            {/* Info Card */}
            <div className="rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-4 md:p-6">
                <div className="flex items-start gap-3 md:gap-4">
                    <div className="flex-shrink-0 h-10 w-10 md:h-12 md:w-12 bg-blue-600 rounded-full flex items-center justify-center">
                        <Layers className="h-5 w-5 md:h-6 md:w-6 text-white" />
                    </div>
                    <div>
                        <h3 className="font-semibold text-blue-900 text-sm md:text-base">Cascade Blocks</h3>
                        <p className="text-xs md:text-sm text-blue-700 mt-1">
                            Gera um bloco GAM individual para cada price step da cascata. Suporta steps default ou granulação personalizada.
                            Use o <strong>Revamp</strong> para arquivar blocos queimados e criar novos rapidamente.
                        </p>
                    </div>
                </div>
            </div>

            {/* Filtros de listagem */}
            <div className="rounded-lg bg-white shadow border border-gray-200 p-4 md:p-6">
                <h3 className="text-sm font-semibold text-gray-900 mb-4">Visualizar Cascade Blocks</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <div className="relative">
                        <Label className="text-xs">Domínio</Label>
                        <button
                            type="button"
                            onClick={() => setListDomainOpen(!listDomainOpen)}
                            className="w-full flex items-center justify-between h-9 px-3 bg-white border border-gray-300 rounded-md hover:border-gray-400 transition-colors text-sm"
                        >
                            <div className="flex items-center gap-2 min-w-0">
                                {listSelectedDomain ? (
                                    <>
                                        <img src={getFaviconUrl(listSelectedDomain.domain)} className="h-4 w-4 rounded flex-shrink-0" alt="" />
                                        <span className="font-medium text-gray-900 truncate">{listSelectedDomain.name || listSelectedDomain.domain}</span>
                                    </>
                                ) : (
                                    <span className="text-gray-400">Selecione...</span>
                                )}
                            </div>
                            <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform ${listDomainOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {listDomainOpen && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={() => { setListDomainOpen(false); setListDomainSearch(''); }} />
                                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                                    <div className="p-2 border-b border-gray-100">
                                        <div className="relative">
                                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                            <input
                                                type="text"
                                                placeholder="Buscar domínio..."
                                                value={listDomainSearch}
                                                onChange={(e) => setListDomainSearch(e.target.value)}
                                                className="w-full h-8 pl-8 pr-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500"
                                                autoFocus
                                            />
                                        </div>
                                    </div>
                                    <div className="max-h-[200px] overflow-y-auto">
                                        <button
                                            type="button"
                                            onClick={() => { setListDomainId(0); setListDomainOpen(false); setListDomainSearch(''); }}
                                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm text-gray-400"
                                        >
                                            Selecione...
                                            {listDomainId === 0 && <Check className="h-3.5 w-3.5 ml-auto text-blue-600" />}
                                        </button>
                                        {filteredListDomains.map((d) => (
                                            <button
                                                key={d.id}
                                                type="button"
                                                onClick={() => { setListDomainId(d.id); setListDomainOpen(false); setListDomainSearch(''); }}
                                                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                                            >
                                                <img src={getFaviconUrl(d.domain)} className="h-4 w-4 rounded" alt="" />
                                                <span className="text-gray-800">{d.name || d.domain}</span>
                                                {listDomainId === d.id && <Check className="h-3.5 w-3.5 ml-auto text-blue-600" />}
                                            </button>
                                        ))}
                                        {filteredListDomains.length === 0 && (
                                            <div className="px-3 py-3 text-center text-sm text-gray-400">Nenhum domínio encontrado</div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <div className="relative">
                        <Label className="text-xs">Tipo</Label>
                        <button
                            type="button"
                            onClick={() => setListBlockTypeOpen(!listBlockTypeOpen)}
                            className="w-full flex items-center justify-between h-9 px-3 bg-white border border-gray-300 rounded-md hover:border-gray-400 transition-colors text-sm"
                        >
                            <span className={listBlockType ? 'font-medium text-gray-900' : 'text-gray-400'}>
                                {listBlockType ? CASCADE_BLOCK_TYPES.find(t => t.value === listBlockType)?.label : 'Todos'}
                            </span>
                            <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform ${listBlockTypeOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {listBlockTypeOpen && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={() => setListBlockTypeOpen(false)} />
                                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => { setListBlockType(''); setListBlockTypeOpen(false); }}
                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm text-gray-500"
                                    >
                                        Todos
                                        {!listBlockType && <Check className="h-3.5 w-3.5 ml-auto text-blue-600" />}
                                    </button>
                                    {CASCADE_BLOCK_TYPES.map((t) => (
                                        <button
                                            key={t.value}
                                            type="button"
                                            onClick={() => { setListBlockType(t.value); setListBlockTypeOpen(false); }}
                                            className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                                        >
                                            <span className="text-gray-800">{t.label}</span>
                                            {listBlockType === t.value && <Check className="h-3.5 w-3.5 ml-auto text-blue-600" />}
                                        </button>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="relative">
                        <Label className="text-xs">Formato</Label>
                        <button
                            type="button"
                            onClick={() => setListFormatTypeOpen(!listFormatTypeOpen)}
                            className="w-full flex items-center justify-between h-9 px-3 bg-white border border-gray-300 rounded-md hover:border-gray-400 transition-colors text-sm"
                        >
                            <div className="flex items-center gap-2">
                                {listFormatType ? (
                                    <>
                                        {listFormatType === 'MOBILE' ? <Smartphone className="h-3.5 w-3.5 text-green-600" /> : <Monitor className="h-3.5 w-3.5 text-purple-600" />}
                                        <span className="font-medium text-gray-900">{FORMAT_TYPES.find(f => f.value === listFormatType)?.label}</span>
                                    </>
                                ) : (
                                    <span className="text-gray-400">Todos</span>
                                )}
                            </div>
                            <ChevronDown className={`h-3.5 w-3.5 text-gray-400 flex-shrink-0 transition-transform ${listFormatTypeOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {listFormatTypeOpen && (
                            <>
                                <div className="fixed inset-0 z-10" onClick={() => setListFormatTypeOpen(false)} />
                                <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                                    <button
                                        type="button"
                                        onClick={() => { setListFormatType(''); setListFormatTypeOpen(false); }}
                                        className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm text-gray-500"
                                    >
                                        Todos
                                        {!listFormatType && <Check className="h-3.5 w-3.5 ml-auto text-blue-600" />}
                                    </button>
                                    {FORMAT_TYPES.map((f) => {
                                        const Icon = f.icon;
                                        return (
                                            <button
                                                key={f.value}
                                                type="button"
                                                onClick={() => { setListFormatType(f.value); setListFormatTypeOpen(false); }}
                                                className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm"
                                            >
                                                <Icon className={`h-3.5 w-3.5 ${f.value === 'MOBILE' ? 'text-green-600' : 'text-purple-600'}`} />
                                                <span className="text-gray-800">{f.label}</span>
                                                {listFormatType === f.value && <Check className="h-3.5 w-3.5 ml-auto text-blue-600" />}
                                            </button>
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="flex items-end">
                        <label className="flex items-center gap-2 h-9 text-sm">
                            <input
                                type="checkbox"
                                checked={includeArchived}
                                onChange={(e) => setIncludeArchived(e.target.checked)}
                                className="rounded"
                            />
                            <Archive className="h-3.5 w-3.5 text-gray-500" />
                            Arquivados
                        </label>
                    </div>
                    <div className="flex items-end">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => refetchBlocks()}
                            disabled={!listDomainId}
                            className="w-full"
                        >
                            <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                            Atualizar
                        </Button>
                    </div>
                </div>
            </div>

            {/* Resultado - Blocos agrupados */}
            {listDomainId > 0 && isLoadingBlocks && (
                <div className="rounded-lg bg-white p-8 shadow text-center text-gray-500">
                    Carregando blocos cascade...
                </div>
            )}

            {listDomainId > 0 && !isLoadingBlocks && cascadeData && (
                <div className="space-y-4">
                    {/* Counters */}
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Layers className="h-4 w-4" />
                            <span><strong>{cascadeData.total_blocks}</strong> blocos</span>
                        </div>
                        {activeGroups.length > 0 && (
                            <Badge variant="success">{activeGroups.length} grupo(s) ativo(s)</Badge>
                        )}
                        {archivedGroups.length > 0 && (
                            <Badge variant="secondary">{archivedGroups.length} arquivado(s)</Badge>
                        )}
                    </div>

                    {/* Groups */}
                    {cascadeData.groups.length === 0 && (
                        <div className="rounded-lg bg-white p-8 shadow text-center">
                            <Layers className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">Nenhum cascade block encontrado para este domínio.</p>
                            <Button
                                onClick={() => setIsGenerateOpen(true)}
                                className="mt-4 bg-gradient-to-r from-blue-600 to-indigo-600"
                                size="sm"
                            >
                                <Zap className="mr-2 h-4 w-4" />
                                Gerar Primeiro Lote
                            </Button>
                        </div>
                    )}

                    {cascadeData.groups.map((group: CascadeBlockGroup) => {
                        const isExpanded = expandedGroups.includes(group.cascade_group);
                        const isActive = group.status === 'ACTIVE';
                        const blockTypes = [...new Set(group.blocks.map((b) => b.block_type))];
                        const formats = [...new Set(group.blocks.map((b) => b.format_type))];
                        const prices = group.blocks.map((b) => b.price).sort((a, b) => b - a);

                        return (
                            <div
                                key={group.cascade_group}
                                className={`rounded-lg bg-white shadow border ${isActive ? 'border-blue-200' : 'border-gray-200 opacity-70'}`}
                            >
                                {/* Group Header */}
                                <button
                                    onClick={() => toggleGroup(group.cascade_group)}
                                    className="w-full p-4 md:p-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors rounded-t-lg"
                                >
                                    <div className="flex items-center gap-3">
                                        {isExpanded ? (
                                            <ChevronDown className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <ChevronRight className="h-5 w-5 text-gray-400" />
                                        )}
                                        <div>
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold text-gray-900">
                                                    Lote {group.cascade_group}
                                                </span>
                                                {getStatusBadge(group.status)}
                                            </div>
                                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                                                <span className="flex items-center gap-1">
                                                    <Hash className="h-3 w-3" />
                                                    {group.blocks.length} blocos
                                                </span>
                                                <span>{blockTypes.join(', ')}</span>
                                                <span>{formats.join(', ')}</span>
                                                {prices.length > 0 && (
                                                    <span className="flex items-center gap-1">
                                                        <DollarSign className="h-3 w-3" />
                                                        {Math.min(...prices)} - {Math.max(...prices)}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </button>

                                {/* Group Blocks */}
                                {isExpanded && (
                                    <div className="border-t border-gray-100 p-4 md:p-5">
                                        <div className="grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                            {group.blocks
                                                .sort((a, b) => b.price - a.price)
                                                .map((block: CascadeBlock) => (
                                                    <div
                                                        key={block.id}
                                                        className={`p-3 rounded-lg border transition-colors ${
                                                            block.ad_unit_status === 'ACTIVE'
                                                                ? 'border-gray-200 hover:border-blue-300'
                                                                : 'border-gray-100 bg-gray-50 opacity-60'
                                                        }`}
                                                    >
                                                        <div className="flex items-center justify-between mb-1.5">
                                                            <div className="flex items-center gap-1.5">
                                                                {block.format_type === 'MOBILE' ? (
                                                                    <Smartphone className="h-3.5 w-3.5 text-green-600" />
                                                                ) : (
                                                                    <Monitor className="h-3.5 w-3.5 text-purple-600" />
                                                                )}
                                                                <span className="text-sm font-medium text-gray-900">
                                                                    {block.block_type}
                                                                </span>
                                                            </div>
                                                            <span className="text-sm font-bold text-blue-700">
                                                                ${block.price}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center justify-between">
                                                            {getStatusBadge(block.ad_unit_status)}
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="h-6 px-1.5"
                                                                onClick={() => copyPath(block.full_path)}
                                                                title="Copiar path"
                                                            >
                                                                <Copy className="h-3 w-3" />
                                                            </Button>
                                                        </div>
                                                        <p
                                                            className="text-xs text-gray-400 mt-1 truncate"
                                                            title={block.ad_unit_name}
                                                        >
                                                            {block.ad_unit_name}
                                                        </p>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Empty state sem domínio selecionado */}
            {listDomainId === 0 && (
                <div className="rounded-lg bg-white p-8 md:p-12 shadow text-center">
                    <Layers className="h-12 w-12 md:h-16 md:w-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-base md:text-lg font-medium text-gray-900 mb-2">
                        Selecione um domínio
                    </h3>
                    <p className="text-sm text-gray-600">
                        Escolha um domínio acima para visualizar os cascade blocks existentes.
                    </p>
                </div>
            )}

            {/* ========== GENERATE DIALOG ========== */}
            <Dialog open={isGenerateOpen} onOpenChange={setIsGenerateOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Gerar Cascade Blocks</DialogTitle>
                        <DialogDescription>
                            Cria blocos GAM individuais para cada price step da cascata
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-5 mt-4">
                        {/* Domínio */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <div className="p-1 bg-blue-100 rounded"><Globe className="h-3.5 w-3.5 text-blue-600" /></div>
                                Domínio
                            </Label>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => !generateMutation.isPending && setGenDomainOpen(!genDomainOpen)}
                                    className={`w-full flex items-center justify-between px-3 py-2.5 bg-white border border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-sm ${generateMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <div className="flex items-center gap-2.5">
                                        {genSelectedDomain ? (
                                            <>
                                                <img src={getFaviconUrl(genSelectedDomain.domain)} className="h-4 w-4 rounded" alt="" />
                                                <span className="font-medium text-gray-900">{genSelectedDomain.name || genSelectedDomain.domain}</span>
                                            </>
                                        ) : (
                                            <span className="text-gray-400">Selecione um domínio</span>
                                        )}
                                    </div>
                                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${genDomainOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {genDomainOpen && (
                                    <>
                                        <div className="fixed inset-0 z-10" onClick={() => { setGenDomainOpen(false); setGenDomainSearch(''); }} />
                                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                                            <div className="p-2 border-b border-gray-100">
                                                <div className="relative">
                                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                                    <input
                                                        type="text"
                                                        placeholder="Buscar domínio..."
                                                        value={genDomainSearch}
                                                        onChange={(e) => setGenDomainSearch(e.target.value)}
                                                        className="w-full h-9 pl-8 pr-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500"
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>
                                            <div className="max-h-[200px] overflow-y-auto">
                                                {filteredGenDomains.map((d) => (
                                                    <button
                                                        key={d.id}
                                                        type="button"
                                                        onClick={() => { setGenDomainId(d.id); setGenDomainOpen(false); setGenDomainSearch(''); }}
                                                        className="w-full px-3 py-2.5 text-left hover:bg-gray-50 flex items-center gap-2.5 text-sm"
                                                    >
                                                        <img src={getFaviconUrl(d.domain)} className="h-4 w-4 rounded" alt="" />
                                                        <span className="text-gray-800">{d.name || d.domain}</span>
                                                        {genDomainId === d.id && <Check className="h-4 w-4 ml-auto text-blue-600" />}
                                                    </button>
                                                ))}
                                                {filteredGenDomains.length === 0 && (
                                                    <div className="px-3 py-4 text-center text-sm text-gray-400">Nenhum domínio encontrado</div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Tipo de Bloco */}
                        <div className="space-y-2">
                            <Label>Tipo de Bloco</Label>
                            <div className="grid grid-cols-3 gap-3">
                                {CASCADE_BLOCK_TYPES.map((type) => (
                                    <button
                                        key={type.value}
                                        type="button"
                                        onClick={() => setGenBlockType(type.value)}
                                        disabled={generateMutation.isPending}
                                        className={`p-3 rounded-lg border-2 transition-all text-center font-medium text-sm
                                            ${genBlockType === type.value
                                                ? 'border-blue-600 bg-blue-50 text-blue-900'
                                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                            }`}
                                    >
                                        {type.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Formato */}
                        <div className="space-y-2">
                            <Label>Formato</Label>
                            <div className="grid grid-cols-2 gap-3">
                                {FORMAT_TYPES.map((format) => {
                                    const Icon = format.icon;
                                    return (
                                        <button
                                            key={format.value}
                                            type="button"
                                            onClick={() => setGenFormatType(format.value)}
                                            disabled={generateMutation.isPending}
                                            className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2
                                                ${genFormatType === format.value
                                                    ? 'border-blue-600 bg-blue-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <Icon className="h-5 w-5 text-blue-600" />
                                            <span className="font-medium text-sm">{format.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Modo: Default ou Personalizado */}
                        <div className="space-y-2">
                            <Label>Steps da Cascata</Label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => { setGenMode('default'); setGenSelectedGranulation(null); }}
                                    disabled={generateMutation.isPending}
                                    className={`p-3 rounded-lg border-2 transition-all text-left
                                        ${genMode === 'default'
                                            ? 'border-blue-600 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <p className="font-medium text-sm">Default</p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {defaultSteps?.[genBlockType]?.count || 26} steps fixos
                                    </p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setGenMode('custom')}
                                    disabled={generateMutation.isPending}
                                    className={`p-3 rounded-lg border-2 transition-all text-left
                                        ${genMode === 'custom'
                                            ? 'border-blue-600 bg-blue-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <p className="font-medium text-sm">Personalizado</p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {granulations?.length || 0} granulações
                                    </p>
                                </button>
                            </div>
                        </div>

                        {/* Seleção de Granulação */}
                        {genMode === 'custom' && (
                            <div className="space-y-2">
                                <Label>Selecione uma Granulação</Label>
                                <GranulationSelector
                                    selected={genSelectedGranulation}
                                    onSelect={setGenSelectedGranulation}
                                    disabled={generateMutation.isPending}
                                    accentColor="blue"
                                />
                            </div>
                        )}

                        {/* Preview */}
                        <div className="rounded-lg bg-gray-50 p-4">
                            <div className="flex items-center gap-2 mb-2">
                                <Info className="h-4 w-4 text-gray-500" />
                                <h4 className="font-semibold text-gray-900 text-sm">Resumo da Geração</h4>
                            </div>
                            <ul className="space-y-1 text-sm text-gray-600">
                                <li>
                                    <strong>Domínio:</strong>{' '}
                                    {genDomainId ? domains?.find((d) => d.id === genDomainId)?.name || domains?.find((d) => d.id === genDomainId)?.domain : 'Nenhum'}
                                </li>
                                <li>
                                    <strong>Tipo:</strong> {genBlockType} | <strong>Formato:</strong> {genFormatType}
                                </li>
                                <li>
                                    <strong>Steps:</strong>{' '}
                                    {(() => {
                                        const steps = getStepsForPreview(genBlockType, genMode, genSelectedGranulation);
                                        if (steps.length === 0) return 'Selecione uma granulação';
                                        return `${steps.length} blocos ($${Math.max(...steps)} → $${Math.min(...steps)})`;
                                    })()}
                                </li>
                                {genMode === 'custom' && genSelectedGranulation && (
                                    <li>
                                        <strong>Granulação:</strong> {genSelectedGranulation.name}
                                    </li>
                                )}
                            </ul>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => { setIsGenerateOpen(false); resetGenerateForm(); }}
                                disabled={generateMutation.isPending}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleGenerate}
                                disabled={!genDomainId || generateMutation.isPending || (genMode === 'custom' && !genSelectedGranulation)}
                                className="flex-1 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                            >
                                {generateMutation.isPending ? 'Gerando...' : (
                                    <>
                                        <Zap className="mr-2 h-4 w-4" />
                                        Gerar Blocos
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* ========== REVAMP DIALOG ========== */}
            <Dialog open={isRevampOpen} onOpenChange={setIsRevampOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <RefreshCw className="h-5 w-5 text-orange-600" />
                            Revamp Cascade Blocks
                        </DialogTitle>
                        <DialogDescription>
                            Arquiva todos os blocos cascade ativos e cria um novo lote com data atual
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-5 mt-4">
                        {/* Warning */}
                        <div className="rounded-lg bg-orange-50 border border-orange-200 p-4">
                            <div className="flex items-start gap-3">
                                <Archive className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                                <div className="text-sm text-orange-800">
                                    <p className="font-semibold mb-1">Atenção</p>
                                    <p>
                                        Todos os blocos cascade <strong>ativos</strong> do tipo/formato selecionado
                                        serão <strong>arquivados</strong> no GAM e no banco. Novos blocos serão
                                        criados com um nome diferente (data atual).
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Domínio */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                                <div className="p-1 bg-orange-100 rounded"><Globe className="h-3.5 w-3.5 text-orange-600" /></div>
                                Domínio
                            </Label>
                            <div className="relative">
                                <button
                                    type="button"
                                    onClick={() => !revampMutation.isPending && setRevDomainOpen(!revDomainOpen)}
                                    className={`w-full flex items-center justify-between px-3 py-2.5 bg-white border border-gray-300 rounded-lg hover:border-gray-400 transition-colors text-sm ${revampMutation.isPending ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <div className="flex items-center gap-2.5">
                                        {revSelectedDomain ? (
                                            <>
                                                <img src={getFaviconUrl(revSelectedDomain.domain)} className="h-4 w-4 rounded" alt="" />
                                                <span className="font-medium text-gray-900">{revSelectedDomain.name || revSelectedDomain.domain}</span>
                                            </>
                                        ) : (
                                            <span className="text-gray-400">Selecione um domínio</span>
                                        )}
                                    </div>
                                    <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${revDomainOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {revDomainOpen && (
                                    <>
                                        <div className="fixed inset-0 z-10" onClick={() => { setRevDomainOpen(false); setRevDomainSearch(''); }} />
                                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg overflow-hidden">
                                            <div className="p-2 border-b border-gray-100">
                                                <div className="relative">
                                                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                                                    <input
                                                        type="text"
                                                        placeholder="Buscar domínio..."
                                                        value={revDomainSearch}
                                                        onChange={(e) => setRevDomainSearch(e.target.value)}
                                                        className="w-full h-9 pl-8 pr-3 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500"
                                                        autoFocus
                                                    />
                                                </div>
                                            </div>
                                            <div className="max-h-[200px] overflow-y-auto">
                                                {filteredRevDomains.map((d) => (
                                                    <button
                                                        key={d.id}
                                                        type="button"
                                                        onClick={() => { setRevDomainId(d.id); setRevDomainOpen(false); setRevDomainSearch(''); }}
                                                        className="w-full px-3 py-2.5 text-left hover:bg-gray-50 flex items-center gap-2.5 text-sm"
                                                    >
                                                        <img src={getFaviconUrl(d.domain)} className="h-4 w-4 rounded" alt="" />
                                                        <span className="text-gray-800">{d.name || d.domain}</span>
                                                        {revDomainId === d.id && <Check className="h-4 w-4 ml-auto text-blue-600" />}
                                                    </button>
                                                ))}
                                                {filteredRevDomains.length === 0 && (
                                                    <div className="px-3 py-4 text-center text-sm text-gray-400">Nenhum domínio encontrado</div>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Tipo */}
                        <div className="space-y-2">
                            <Label>Tipo de Bloco</Label>
                            <div className="grid grid-cols-3 gap-3">
                                {CASCADE_BLOCK_TYPES.map((type) => (
                                    <button
                                        key={type.value}
                                        type="button"
                                        onClick={() => setRevBlockType(type.value)}
                                        disabled={revampMutation.isPending}
                                        className={`p-3 rounded-lg border-2 transition-all text-center font-medium text-sm
                                            ${revBlockType === type.value
                                                ? 'border-orange-500 bg-orange-50 text-orange-900'
                                                : 'border-gray-200 hover:border-gray-300 text-gray-700'
                                            }`}
                                    >
                                        {type.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Formato */}
                        <div className="space-y-2">
                            <Label>Formato</Label>
                            <div className="grid grid-cols-2 gap-3">
                                {FORMAT_TYPES.map((format) => {
                                    const Icon = format.icon;
                                    return (
                                        <button
                                            key={format.value}
                                            type="button"
                                            onClick={() => setRevFormatType(format.value)}
                                            disabled={revampMutation.isPending}
                                            className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2
                                                ${revFormatType === format.value
                                                    ? 'border-orange-500 bg-orange-50'
                                                    : 'border-gray-200 hover:border-gray-300'
                                                }`}
                                        >
                                            <Icon className="h-5 w-5 text-orange-600" />
                                            <span className="font-medium text-sm">{format.label}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Modo novos blocos */}
                        <div className="space-y-2">
                            <Label>Steps dos novos blocos</Label>
                            <div className="grid grid-cols-2 gap-3">
                                <button
                                    type="button"
                                    onClick={() => { setRevMode('default'); setRevSelectedGranulation(null); }}
                                    disabled={revampMutation.isPending}
                                    className={`p-3 rounded-lg border-2 transition-all text-left
                                        ${revMode === 'default'
                                            ? 'border-orange-500 bg-orange-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <p className="font-medium text-sm">Default</p>
                                    <p className="text-xs text-gray-500 mt-0.5">Mesmos steps fixos</p>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setRevMode('custom')}
                                    disabled={revampMutation.isPending}
                                    className={`p-3 rounded-lg border-2 transition-all text-left
                                        ${revMode === 'custom'
                                            ? 'border-orange-500 bg-orange-50'
                                            : 'border-gray-200 hover:border-gray-300'
                                        }`}
                                >
                                    <p className="font-medium text-sm">Personalizado</p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                        {granulations?.length || 0} granulações
                                    </p>
                                </button>
                            </div>
                        </div>

                        {revMode === 'custom' && (
                            <div className="space-y-2">
                                <Label>Selecione uma Granulação</Label>
                                <GranulationSelector
                                    selected={revSelectedGranulation}
                                    onSelect={setRevSelectedGranulation}
                                    disabled={revampMutation.isPending}
                                    accentColor="orange"
                                />
                            </div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-3 pt-2">
                            <Button
                                variant="outline"
                                className="flex-1"
                                onClick={() => { setIsRevampOpen(false); resetRevampForm(); }}
                                disabled={revampMutation.isPending}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleRevamp}
                                disabled={!revDomainId || revampMutation.isPending || (revMode === 'custom' && !revSelectedGranulation)}
                                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
                            >
                                {revampMutation.isPending ? 'Processando...' : (
                                    <>
                                        <RefreshCw className="mr-2 h-4 w-4" />
                                        Executar Revamp
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
